import { type FC, useId, useMemo } from "react";
import Tab from "@mui/material/Tab";
import { ErrorBoundaryWithAsyncContext } from "../ErrorBoundary";
import type { ContractDetailTabsProps } from "./ContractDetailTabs.types";
import * as Styled from "./ContractDetailTabs.styled";

const getTabId = (idPrefix: string, slug: string) => `${idPrefix}-tab-${slug}`;
const getTabPanelId = (idPrefix: string, slug: string) => `${idPrefix}-tabpanel-${slug}`;

export const ContractDetailTabs: FC<ContractDetailTabsProps> = ({
    tabs,
    tabIndex,
    onTabChange,
    header,
    sideContent,
    fixTabViewportWidth,
    onTouchStart,
    onTouchEnd,
    children,
}) => {
    const childArray = useMemo(() => (Array.isArray(children) ? children : [children]).filter(Boolean), [children]);
    const tabIdPrefix = useId().replace(/:/g, "");
    const hasSideContent = Boolean(sideContent);

    return (
        <Styled.TabsRoot>
            <Styled.TabBarRow>
                <Styled.TabBarArea $hasSideContent={hasSideContent}>
                    {header}
                    <Styled.TabsViewport $fixTabViewportWidth={fixTabViewportWidth}>
                        <Styled.StyledTabs
                            value={tabIndex}
                            onChange={(_, newValue: number) => onTabChange(newValue)}
                            variant="scrollable"
                            scrollButtons={false}
                            $fixTabViewportWidth={fixTabViewportWidth}
                        >
                            {tabs.map(({ label, slug, disabled }) => (
                                <Tab
                                    key={slug}
                                    label={label}
                                    disabled={disabled ?? false}
                                    id={getTabId(tabIdPrefix, slug)}
                                    aria-controls={getTabPanelId(tabIdPrefix, slug)}
                                />
                            ))}
                        </Styled.StyledTabs>
                    </Styled.TabsViewport>
                </Styled.TabBarArea>
                {sideContent}
            </Styled.TabBarRow>

            <Styled.PanelsContainer onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                {childArray.map((child, i) => {
                    const currentTab = tabs[i];

                    return (
                        <Styled.TabPanel
                            key={currentTab?.slug ?? i}
                            role="tabpanel"
                            visible={tabIndex === i}
                            hidden={tabIndex !== i}
                            id={currentTab ? getTabPanelId(tabIdPrefix, currentTab.slug) : undefined}
                            aria-labelledby={currentTab ? getTabId(tabIdPrefix, currentTab.slug) : undefined}
                        >
                            {tabIndex === i && (
                                <ErrorBoundaryWithAsyncContext>
                                    <Styled.TabPanelContent>{child}</Styled.TabPanelContent>
                                </ErrorBoundaryWithAsyncContext>
                            )}
                        </Styled.TabPanel>
                    );
                })}
            </Styled.PanelsContainer>
        </Styled.TabsRoot>
    );
};
